import { PayloadAction, createAction, createSlice } from '@reduxjs/toolkit';
import { cloneDeep } from 'lodash';
import { Preferences } from '../../profile/objects/preferences';
import { Column } from '../../cms/objects/column';
import {
    LockableFareActiveBooking,
    LockableFareActiveBookingListCriteria,
    LockableFareKpis,
} from '../objects/lockableFareActiveBooking';
import {
    EMPTY_LOCKABLE_FARE_ACTIVE_BOOKING_LIST_CONFIGURATION,
    EMPTY_LOCKABLE_FARE_ACTIVE_BOOKING_LIST_CRITERIA,
} from '../constant/lockableFareActiveBookingConstant';
import { setLockableFareActiveBookingPreference } from '../utils/setLockableFareActiveBookingPreference';

export const setPreferences = createAction<Preferences[]>('PROFILE_SET_PREFERENCES');

interface LockableFareActiveBookingsState {
    list: LockableFareActiveBooking[];
    list_count: number;
    list_loading: boolean;
    list_criteria: LockableFareActiveBookingListCriteria;
    list_configuration: { columns: Column[]; preferences: Preferences[] };
    kpis: LockableFareKpis | null;
    kpis_loading: boolean;
    refresh: boolean;
    first_preference_loaded: boolean;
    selected_items: LockableFareActiveBooking[];
    history_item: LockableFareActiveBooking | null;
}

const initialState: LockableFareActiveBookingsState = {
    list: [],
    list_count: 0,
    list_loading: false,
    list_criteria: EMPTY_LOCKABLE_FARE_ACTIVE_BOOKING_LIST_CRITERIA,
    list_configuration: EMPTY_LOCKABLE_FARE_ACTIVE_BOOKING_LIST_CONFIGURATION,
    kpis: null,
    kpis_loading: false,
    refresh: false,
    first_preference_loaded: false,
    selected_items: [],
    history_item: null,
};

export const lockableFareActiveBookings = createSlice({
    name: 'lockable_fare_active_bookings',
    initialState,
    reducers: {
        updateList(state, action: PayloadAction<LockableFareActiveBooking[]>) {
            state.list = action.payload || [];
        },
        updateListCount(state, action: PayloadAction<number>) {
            state.list_count = action.payload;
        },
        updateListLoading(state, action: PayloadAction<boolean>) {
            state.list_loading = action.payload;
        },
        updateListCriteria(state, action: PayloadAction<LockableFareActiveBookingListCriteria>) {
            state.list_criteria = action.payload;
        },
        updateKpis(state, action: PayloadAction<LockableFareKpis | null>) {
            state.kpis = action.payload;
        },
        updateKpisLoading(state, action: PayloadAction<boolean>) {
            state.kpis_loading = action.payload;
        },
        toggleRefresh(state) {
            state.refresh = !state.refresh;
        },
        addListConfigurationPreference(state, action: PayloadAction<Preferences[]>) {
            const preferences = cloneDeep(state.list_configuration.preferences);
            action.payload.forEach(p => preferences.push(p));
            state.list_configuration.preferences = preferences;
        },
        setListConfigurationPreference(state, action: PayloadAction<Preferences[]>) {
            state.list_configuration.preferences = action.payload;
        },
        setColumns(state, action: PayloadAction<Column[]>) {
            state.list_configuration.columns = action.payload;
        },
        toggleColumn(state, action: PayloadAction<number>) {
            const columns = cloneDeep(state.list_configuration.columns);
            columns[action.payload].checked = !columns[action.payload].checked;
            state.list_configuration.columns = columns;
        },
        toggleSelectItem(state, action: PayloadAction<LockableFareActiveBooking>) {
            const exists = state.selected_items.some(i => i.id === action.payload.id);
            if (exists) {
                state.selected_items = state.selected_items.filter(i => i.id !== action.payload.id);
            } else {
                const items = cloneDeep(state.selected_items);
                items.push(action.payload);
                state.selected_items = items;
            }
        },
        setSelectedItems(state, action: PayloadAction<LockableFareActiveBooking[]>) {
            state.selected_items = action.payload;
        },
        clearSelection(state) {
            state.selected_items = [];
        },
        setHistoryItem(state, action: PayloadAction<LockableFareActiveBooking | null>) {
            state.history_item = action.payload;
        },
        clearHistoryItem(state) {
            state.history_item = null;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(setPreferences, (state, action) => {
            const result = setLockableFareActiveBookingPreference(action.payload, state.list_configuration.columns);
            state.list_configuration.columns = result.columns;
            state.list_configuration.preferences = result.preferences;
            if (result.order_by) {
                state.list_criteria.order_by = result.order_by;
                state.list_criteria.order = result.order;
            }
            state.first_preference_loaded = true;
        });
    },
});

export const {
    updateList,
    updateListCount,
    updateListLoading,
    updateListCriteria,
    updateKpis,
    updateKpisLoading,
    toggleRefresh,
    addListConfigurationPreference,
    setListConfigurationPreference,
    setColumns,
    toggleColumn,
    toggleSelectItem,
    setSelectedItems,
    clearSelection,
    setHistoryItem,
    clearHistoryItem,
} = lockableFareActiveBookings.actions;
